import React, { PropsWithChildren, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import i18n from 'i18next';
import colors from '../../Theming/colors';
import timColors from '../../Theming/TIMTheme/colors';
import { Helmet } from 'react-helmet';
import timIcon from '../../Assets/PartnerNewpluginsdkActivate/tim-ios-logo.png';
import clsx from 'clsx';
import '../../Theming/Fonts/custom-fonts.css';

const Container = styled.div`
  width: 100%;
  max-width: 700px;
  min-height: 700px;
  margin: 0 auto;
  font-family: TIMSans-Regular;
  background-color: ${timColors.darkCloudBlue};

  &.showRoyalBlueBackground {
    background-color: ${timColors.darkRoyalBlue};
  }
`;

const Body = styled.div<{ bodyMarginTop?: number }>`
  margin-top: ${(props) => (props.bodyMarginTop ? props.bodyMarginTop : -3)}px;
  min-height: 500px;
  padding-top: 1px;

  &.whiteBackground {
    background-color: white;
  }
`;

const Header = styled.div`
  height: 340px;
  position: relative;

  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;

  &.showSmallHeight {
    height: 200px;
  }

  &.showSuccessPageHeight {
    height: 243px;
  }
`;

const HeaderText = styled.div`
  text-align: center;
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%;
  color: ${colors.white};
  width: 250px;
  z-index: 2;
  padding-top: 30px;

  &.shadow {
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.6);
    padding-top: 45px;
  }
`;

const SubHeaderText = styled.div`
  text-align: center;
  font-size: 20px;
  font-style: normal;
  color: ${colors.white};
  z-index: 2;
  padding-top: 6px;
`;

const BoldText = styled.div`
  font-weight: bold;
`;

const StyledBrandedIcon = styled.img`
  width: 60px;
  height: 60px;

  &.successPageView {
    width: 357.4px;
    height: 93.56px;
  }

  &.partialPageView {
    width: 97.23px;
    height: 79.13px;
  }
`;

const StyledBrandedImage = styled.img`
  width: 100%;
  height: 342px;

  object-fit: cover;
  margin-bottom: -2px;
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  display: inline-block;
  height: calc(100% - 2px);
  width: 100%;
  position: absolute;
  z-index: 1;
  top: 0;
`;

interface MultiStepContainerProps extends PropsWithChildren {
  headerText?: string;
  headerTextWidth?: number;
  subHeaderText?: string;
  bodyMarginTop?: number;
  image?: string;
  logo?: string;
  headerShadow?: boolean;
  successPageView?: boolean;
  partialPageView?: boolean;
}

const MultiStepActivationContainer: React.FC<MultiStepContainerProps> = ({
  children,
  headerText,
  bodyMarginTop,
  image,
  logo,
  headerShadow = false,
  successPageView = false,
  subHeaderText,
  partialPageView,
}) => {
  if (i18n.language != 'it') {
    i18n.changeLanguage('it');
  }
  const { t } = useTranslation('');

  return (
    <Container
      className={clsx({
        showRoyalBlueBackground:
          logo != undefined && !successPageView && !partialPageView,
      })}
      id="activationContainer"
    >
      <Helmet>
        <link rel="icon" type="image/png" sizes="16x16" href={timIcon} />
        <title>TIM Benefit</title>
      </Helmet>
      <Header
        className={clsx({
          showSmallHeight: logo != undefined,
          showSuccessPageHeight: successPageView,
        })}
        id="activationHeader"
      >
        {logo && (
          <StyledBrandedIcon
            className={clsx({ successPageView, partialPageView })}
            id="activationHeaderLogo"
            src={logo}
            alt=" brand-icon"
          />
        )}
        {headerText && (
          <HeaderText
            className={clsx({ shadow: headerShadow })}
            id="activationHeaderText"
          >
            <Trans t={t} i18nKey={headerText}>
              <BoldText>AbsoluteText</BoldText>
            </Trans>
          </HeaderText>
        )}
        {subHeaderText && (
          <SubHeaderText id="activationSubHeaderText">
            <Trans t={t} i18nKey={subHeaderText}>
              <BoldText>AbsoluteText</BoldText>
            </Trans>
          </SubHeaderText>
        )}
        {image && (
          <ImageWrapper>
            <StyledBrandedImage
              id="activationHeaderImage"
              src={image}
              alt=" image"
            />
          </ImageWrapper>
        )}
      </Header>
      <Body
        className={clsx({
          whiteBackground: logo != undefined && !partialPageView,
        })}
        bodyMarginTop={bodyMarginTop}
      >
        {children}
      </Body>
    </Container>
  );
};

export default MultiStepActivationContainer;
